import type { Score, ScoreNote } from "./types";

/**
 * Read any piece in any key — and in the parallel minor.
 *
 * Jason's own note on Twinkle asks the student to do exactly this ("as you learn
 * up your scales, try playing this melody on various key signatures"), so the
 * book already sets the exercise; this is the page finally being able to do it.
 *
 * Three rules keep the result musically honest.
 *
 *  1. SPELLING COMES FROM THE KEY, NOT FROM A PITCH-CLASS TABLE. A pitch class
 *     knows a note is "the black key between D and E"; it does not know whether
 *     to print D♯ or E♭. Every transposed note is therefore spelled by finding
 *     the LETTER the target key uses for that pitch. That is what makes F♯ major
 *     print E♯ rather than F, and G♭ major print C♭ rather than B — and a student
 *     reading the note aloud has to say the right name.
 *
 *  2. THE MODE IS A REAL TRANSFORMATION, NOT A RELABEL. Asking for C minor does
 *     not merely redraw the key signature: the third, sixth and seventh degrees
 *     are lowered a semitone, which is what actually turns a major tune into its
 *     parallel natural minor. A note that was ALREADY lowered — a blues flat
 *     third, say — is left alone rather than flattened twice.
 *
 *  3. REGISTER IS PRESERVED. The shift takes the shorter way round the circle
 *     (never more than a tritone), so a beginner's piece does not climb out of
 *     the hand position it was written for, and `lowest`/`highest` are
 *     recomputed so everything downstream — the graded exercise generator, the
 *     range readout — sees the piece that is actually on the page.
 *
 * `fifths` carries the key signature. The renderer draws it AND hands it to
 * VexFlow's accidental pass, so setting it correctly here is what makes the
 * engraving right, not just the pitches.
 */

export type Mode = "major" | "minor";
/** Which minor: natural leaves the seventh flat, harmonic raises it. */
export type MinorKind = "natural" | "harmonic";

export interface ReadingKey {
  /** Display name, e.g. "E♭" or "C minor". */
  name: string;
  /** Tonic on its own, e.g. "E♭". */
  tonic: string;
  /** Position on the circle of fifths: + sharps, − flats. */
  fifths: number;
  /** Pitch class of the tonic. */
  pc: number;
  mode: Mode;
}

const FLAT = "♭";

/** The twelve major keys, in chromatic order. */
export const MAJOR_KEYS: ReadingKey[] = [
  ["C", 0, 0], ["D" + FLAT, -5, 1], ["D", 2, 2], ["E" + FLAT, -3, 3],
  ["E", 4, 4], ["F", -1, 5], ["F♯", 6, 6], ["G", 1, 7],
  ["A" + FLAT, -4, 8], ["A", 3, 9], ["B" + FLAT, -2, 10], ["B", 5, 11],
].map(([tonic, fifths, pc]) => ({
  name: tonic as string, tonic: tonic as string,
  fifths: fifths as number, pc: pc as number, mode: "major" as const,
}));

/**
 * The twelve minor keys, each carrying its relative major's signature — the
 * signature a natural minor is written with.
 */
export const MINOR_KEYS: ReadingKey[] = [
  ["C", -3, 0], ["C♯", 4, 1], ["D", -1, 2], ["E" + FLAT, -6, 3],
  ["E", 1, 4], ["F", -4, 5], ["F♯", 3, 6], ["G", -2, 7],
  ["G♯", 5, 8], ["A", 0, 9], ["B" + FLAT, -5, 10], ["B", 2, 11],
].map(([tonic, fifths, pc]) => ({
  name: `${tonic} minor`, tonic: tonic as string,
  fifths: fifths as number, pc: pc as number, mode: "minor" as const,
}));

/** Kept for callers that only ever offered majors. */
export const TRANSPOSE_KEYS = MAJOR_KEYS;

/* ── spelling ───────────────────────────────────────────────────────── */

const LETTERS = ["C", "D", "E", "F", "G", "A", "B"] as const;
const LETTER_PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const SHARP_ORDER = ["F", "C", "G", "D", "A", "E", "B"];
const FLAT_ORDER = ["B", "E", "A", "D", "G", "C", "F"];
/** Semitones above the tonic for degrees 1..7 of a major scale. */
const MAJOR_SEMIS = [0, 2, 4, 5, 7, 9, 11];

const mod = (n: number, m: number) => ((n % m) + m) % m;

/** Letter → alteration for a key signature. */
export function keyAccidentals(fifths: number): Record<string, number> {
  const out: Record<string, number> = {};
  if (fifths > 0) for (const l of SHARP_ORDER.slice(0, fifths)) out[l] = 1;
  if (fifths < 0) for (const l of FLAT_ORDER.slice(0, -fifths)) out[l] = -1;
  return out;
}

function nameFor(letter: string, alter: number, midi: number): string {
  // The octave is named from C, and an accidental can push a letter across the
  // C boundary: C♭4 sounds as B3 but is still written in octave 4.
  const octave = Math.floor((midi - alter) / 12) - 1;
  const acc = alter > 0 ? "#".repeat(alter) : alter < 0 ? "b".repeat(-alter) : "";
  return `${letter}${acc}${octave}`;
}

/**
 * Spell a MIDI note the way the given key signature would print it.
 *
 * Diatonic notes take the key's own letter. A chromatic note is scored against
 * every legal spelling and the most idiomatic one wins: cancelling the key
 * signature with a natural beats inventing a double accidental, and a flat key
 * leans flat while a sharp key leans sharp.
 */
export function spellInKey(midi: number, fifths: number): string {
  const ks = keyAccidentals(fifths);
  const pc = mod(midi, 12);

  for (const letter of LETTERS) {
    const alter = ks[letter] ?? 0;
    if (mod(LETTER_PC[letter] + alter, 12) === pc) return nameFor(letter, alter, midi);
  }

  const lean = fifths < 0 ? -1 : 1;
  let best: { letter: string; alter: number; score: number } | null = null;
  for (const letter of LETTERS) {
    for (let alter = -2; alter <= 2; alter++) {
      if (mod(LETTER_PC[letter] + alter, 12) !== pc) continue;
      let score = Math.abs(alter) * 2;
      if (alter === 0) score -= 3;                                  // a natural cancels the signature
      else if (Math.sign(alter) === lean) score -= 1;               // lean the way the key leans
      if (Math.abs(alter - (ks[letter] ?? 0)) > 1) score += 4;      // never more than a semitone off the degree
      if (!best || score < best.score) best = { letter, alter, score };
    }
  }
  return best ? nameFor(best.letter, best.alter, midi) : nameFor("C", 0, midi);
}

/* ── key parsing ────────────────────────────────────────────────────── */

const ascii = (s: string) => s.replace(/♭/g, "b").replace(/♯/g, "#").trim();

/**
 * Normalise a stored key name to a `ReadingKey`.
 * Accepts "C", "Ab", "A♭", "A minor", "Am", "F# minor".
 */
export function keyChoiceFor(name: string): ReadingKey {
  const raw = ascii(name);
  const minor = /\bmin(or)?\b/i.test(raw) || /^[A-G](#|b)?m$/.test(raw);
  const tonic = raw.replace(/\s*min(or)?\s*$/i, "").replace(/m$/, "").trim();
  const table = minor ? MINOR_KEYS : MAJOR_KEYS;
  const found = table.find((k) => ascii(k.tonic) === tonic);
  return found ?? (minor ? MINOR_KEYS[9] : MAJOR_KEYS[0]);
}

/**
 * How a key should be PRINTED: "E♭ major", "A minor".
 *
 * Everywhere that used to write `${score.key} major` was wrong for the two
 * minor pieces, which announced themselves on the cover as "A MINOR MAJOR" and
 * "F MAJOR" for a piece in D minor. The flat and sharp signs are real glyphs
 * because "Ab" through a CSS uppercase renders as "AB" — the flat turning into
 * a capital B — while a ♭ has no uppercase form and survives.
 */
export function keyLabel(key: string): string {
  const k = keyChoiceFor(key);
  const tonic = k.tonic.replace(/b$/, "♭").replace(/#$/, "♯");
  return k.mode === "minor" ? `${tonic} minor` : `${tonic} major`;
}

/** The key a score is written in, honouring both its name and its signature. */
export function writtenKey(score: Score): ReadingKey {
  const k = keyChoiceFor(score.key);
  // A score whose name and signature disagree is trusted on its signature,
  // because that is what the page actually prints.
  if (k.fifths === score.fifths) return k;
  const table = k.mode === "minor" ? MINOR_KEYS : MAJOR_KEYS;
  return table.find((c) => c.fifths === score.fifths) ?? k;
}

/* ── the transformation ─────────────────────────────────────────────── */

/* ── keeping the piece on the stave ─────────────────────────────────────── */

/** Diatonic step number of a MIDI note, counting letters rather than semitones. */
const LETTER_STEP = [0, 0, 1, 1, 2, 3, 3, 4, 4, 5, 5, 6];
function diatonicStep(midi: number): number {
  const pc = mod(midi, 12);
  const octave = Math.floor(midi / 12) - 1;
  return LETTER_STEP[pc] + 7 * octave;
}

/** Staff extent as diatonic steps: [bottom line, top line]. */
const STAFF: Record<string, [number, number]> = {
  treble: [30, 38],   // E4 … F5
  bass: [18, 26],     // G2 … A3
};

/** How many ledger lines a note needs on a given clef. */
function ledgerLines(midi: number, clef: string): number {
  const s = diatonicStep(midi);
  const [lo, hi] = STAFF[clef] ?? STAFF.treble;
  return Math.max(0, Math.floor((lo - s) / 2), Math.floor((s - hi) / 2));
}

/**
 * Total ledger-line burden of a whole piece at a given shift.
 *
 * Weighted so that ONE note needing four ledgers is worse than many needing
 * one: a reader recognises a note a step off the stave, but has to stop and
 * count anything three or more lines out, and stopping mid-bar is exactly what
 * a sight-reading course exists to cure.
 */
function ledgerBurden(score: Score, shift: number): { worst: number; total: number } {
  let total = 0;
  let worst = 0;
  for (const n of score.notes) {
    if (n.midi == null) continue;
    const clef = score.clefs[n.hand === "rh" ? 0 : 1]
      ?? (n.hand === "rh" ? "treble" : "bass");
    const l = ledgerLines(n.midi + shift, clef);
    total += l * l;
    if (l > worst) worst = l;
    // Anything off an 88-note keyboard is not a choice at all.
    if (n.midi + shift < 21 || n.midi + shift > 108) { total += 10000; worst = 9; }
  }
  return { worst, total };
}

/** How far off the stave a piece reaches, as a count of ledger lines. */
export function ledgerStrain(score: Score): number {
  return ledgerBurden(score, 0).worst;
}

/** Diatonic degree 0..6 of a spelled pitch above a tonic letter. */
function letterDegree(pitch: string, tonicLetter: string): number {
  const l = pitch[0].toUpperCase();
  return mod(LETTERS.indexOf(l as never) - LETTERS.indexOf(tonicLetter as never), 7);
}

/**
 * How far a note sits from the MAJOR scale degree its letter implies, in
 * semitones. 0 means "the plain major-scale note"; −1 means it is already
 * lowered, as a blues third is.
 */
function alterFromMajor(midi: number, pitch: string, tonicPc: number, tonicLetter: string): number {
  const deg = letterDegree(pitch, tonicLetter);
  const wanted = mod(tonicPc + MAJOR_SEMIS[deg], 12);
  return mod(mod(midi, 12) - wanted + 6, 12) - 6;   // nearest signed distance
}

export interface TransposeOptions {
  /** Which flavour of minor to build when moving major → minor. */
  minorKind?: MinorKind;
}

/**
 * Return a copy of `score` sounding in `target`.
 *
 * Returns the original object when nothing needs to change, so React can skip a
 * re-engrave on the common "still in the written key" path.
 */
export function transposeScore(
  score: Score,
  target: string | ReadingKey,
  opts: TransposeOptions = {},
): Score {
  const to = typeof target === "string" ? keyChoiceFor(target) : target;
  const from = writtenKey(score);
  if (from.pc === to.pc && from.fifths === to.fifths && from.mode === to.mode) return score;

  // WHICH OCTAVE. The old rule was "shorter way round the circle, never more
  // than a tritone", which sounds right and reads badly: G Major Run, written
  // G3–C5, goes DOWN five semitones into D and its bottom note lands four ledger
  // lines below the treble stave. Up seven instead and the whole piece stays on
  // the page.
  //
  // So the three candidate octaves are judged by how far off the stave they push
  // the music. The WORST note decides it, not the average — one or two ledgers is
  // still reading, three is counting, and a beginner who stops to count has lost
  // the bar. Total burden breaks a tie, then the smaller move, so a piece still
  // sits near the register it was written in.
  const raw = mod(to.pc - from.pc, 12);
  const base = raw > 6 ? raw - 12 : raw;
  let semitones = base;
  // The piece's own tolerance: whatever it already reaches as written, and never
  // less than two — one or two ledgers is still reading, three is counting, and
  // a beginner who stops to count has lost the bar. The octave is only touched
  // when the shift has pushed the music PAST that, so a plain change of mode or
  // a small move never quietly relocates a piece the student knows.
  const cap = Math.max(2, ledgerBurden(score, 0).worst);
  let here = ledgerBurden(score, base);
  if (here.worst > cap) {
    for (const cand of [base + 12, base - 12]) {
      const b = ledgerBurden(score, cand);
      if (b.worst < here.worst) { here = b; semitones = cand; }
    }
  }

  const changingMode = from.mode !== to.mode;
  const fromTonicLetter = from.tonic[0].toUpperCase();
  const minorKind = opts.minorKind ?? "natural";
  // Which degrees move when the mode changes. Degrees are 0-based: 2 = the
  // third, 5 = the sixth, 6 = the seventh.
  const shifted = changingMode
    ? (to.mode === "minor"
        ? (minorKind === "harmonic" ? [2, 5] : [2, 5, 6])
        : [2, 5, 6])
    : [];
  const direction = to.mode === "minor" ? -1 : 1;

  const notes: ScoreNote[] = score.notes.map((n) => {
    if (n.midi == null || !n.pitch) return n;    // a rest carries no pitch to move
    let midi = n.midi + semitones;
    if (changingMode) {
      const off = alterFromMajor(n.midi, n.pitch, from.pc, fromTonicLetter);
      const deg = letterDegree(n.pitch, fromTonicLetter);
      // Only move a degree that is still in the form we are LEAVING: the plain
      // major degree on the way down, the lowered one on the way back up. A note
      // already inflected — a blues flat third, or A minor's raised seventh —
      // keeps the colour it was given.
      const inSourceForm = to.mode === "minor" ? off === 0 : off === -1;
      if (shifted.includes(deg) && inSourceForm) midi += direction;
    }
    return { ...n, midi, pitch: spellInKey(midi, to.fifths) };
  });

  const sounding = notes.filter((n) => n.midi != null).map((n) => n.midi as number);

  return {
    ...score,
    key: to.name,
    fifths: to.fifths,
    notes,
    lowest: sounding.length ? Math.min(...sounding) : score.lowest,
    highest: sounding.length ? Math.max(...sounding) : score.highest,
  };
}
